import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';

import { generateImportTemplateFromSpreadsheet } from 'utils';

// Containers
import { useJobNotificationContext } from 'containers/common/JobNotificationProvider';

// Components
import { FilesDropzone, Mui, MuiLab } from 'components';
import { Drawer, Stack } from 'components/layouts';

const useStyles = Mui.makeStyles(theme => ({
  link: { '&:hover': { cursor: 'pointer', textDecoration: 'underline' } },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

function Import({ actions, open, onClose, title, description }) {
  // Hooks
  const classes = useStyles();
  const { trackJob } = useJobNotificationContext();
  const { enqueueSnackbar } = useSnackbar();

  // States
  const [selectedAction, setSelectedAction] = useState(null);
  const [dryRun, setDryRun] = useState(false);
  const [loading, setLoading] = useState(false);

  // Actions
  const onSuccess = res => {
    const { job: jobUid } = res?.data || {};
    if (jobUid) {
      trackJob(jobUid);
      enqueueSnackbar('Arquivo enviado com sucesso, em breve você será notificado', {
        variant: 'info',
      });
      setSelectedAction(null);
      setDryRun(false);
      onClose();
    }
  };

  const onError = () =>
    enqueueSnackbar('Não foi possível completar sua requisição', { variant: 'error' });

  const handleImport = ([newFile]) => {
    setLoading(true);
    return selectedAction
      .onSubmit(newFile, dryRun)
      .then(onSuccess)
      .catch(onError)
      .then(() => setLoading(false));
  };

  const handleImportSampleDownload = template =>
    template
      .onDownload()
      .then(res => res.data)
      .then(data =>
        generateImportTemplateFromSpreadsheet(
          data,
          template?.importSampleFileLabel || 'Modelo_Importação',
        ),
      )
      .catch(onError);

  const handleClose = () => {
    onClose();
    setDryRun(false);
    setSelectedAction(null);
  };

  return (
    <Drawer
      title={title}
      description={
        description || (
          <>
            A importação deve ser feita obrigatoriamente a partir de um arquivo{' '}
            <strong>.xlsx</strong>. O modelo está fornecido abaixo. A ordem das colunas não
            importam, mas é importante manter o mesmo nome da coluna para garantir que a rotina de
            importação leia o campo corretamente.
          </>
        )
      }
      open={open}
      onClose={handleClose}>
      <Mui.Container>
        <Mui.Card variant="outlined">
          {!selectedAction && (
            <Mui.List>
              <Mui.ListSubheader>
                <Mui.Typography variant="subtitle1">Selecione uma opção</Mui.Typography>
              </Mui.ListSubheader>
              {actions.map((action, index) => (
                <Mui.ListItem
                  button
                  key={action.key}
                  onClick={() => setSelectedAction(action)}
                  divider={index !== actions.length - 1}>
                  <Mui.ListItemAvatar>
                    <Mui.Avatar className={classes.avatar}>
                      <Mui.Icon>{action.icon}</Mui.Icon>
                    </Mui.Avatar>
                  </Mui.ListItemAvatar>
                  <Mui.ListItemText primary={action.label} />
                </Mui.ListItem>
              ))}
            </Mui.List>
          )}
          {!!selectedAction && (
            <Mui.Box m={2}>
              <Mui.Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<Mui.Icon>navigate_before</Mui.Icon>}
                onClick={() => {
                  setDryRun(false);
                  setSelectedAction(null);
                }}>
                Voltar
              </Mui.Button>
              <Mui.Box mb={1} />

              <Mui.Box mb={1}>
                {!!selectedAction?.templates?.length && (
                  <Stack>
                    {selectedAction.templates.map(template => (
                      <MuiLab.Alert key={template.label} severity="info">
                        <Mui.Link
                          className={classes.link}
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => handleImportSampleDownload(template)}>
                          Clique aqui
                        </Mui.Link>{' '}
                        para baixar o <strong>{template.label}</strong>.
                      </MuiLab.Alert>
                    ))}
                  </Stack>
                )}
              </Mui.Box>
              {selectedAction?.hasDryRun && (
                <Mui.FormControlLabel
                  control={
                    <Mui.Checkbox
                      name="dryRun"
                      checked={dryRun}
                      onChange={() => setDryRun(d => !d)}
                    />
                  }
                  label={<Mui.Typography variant="h6">Simular importação de dados</Mui.Typography>}
                />
              )}
              <FilesDropzone
                loading={loading}
                onSubmit={handleImport}
                acceptTypes="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                maxFiles={1}
              />
            </Mui.Box>
          )}
        </Mui.Card>
      </Mui.Container>
    </Drawer>
  );
}

Import.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      hasDryRun: PropTypes.bool,
      templates: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          importSampleFileLabel: PropTypes.string.isRequired,
          onDownload: PropTypes.func.isRequired,
        }),
      ),
      onSubmit: PropTypes.func.isRequired,
    }),
  ).isRequired,
};

export default Import;
